import { useEffect, useState } from "react";
import {
  login,
  loginWithGoogle,
  resetPassword,
  registerAndSendVerification,
  checkVerificationAndDelete,
} from "@auth/auth";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import JOTR from "@assets/logo/seatrcred.svg";
import seatrcLogo from "@assets/logo/seatrc.svg"; // Use seatrc logo for the branding side if available, or just use JOTR
import bgHero from "@assets/bg/bghero11.jpg";
import { useAuth } from "@context/AuthContext";
import { InputField } from "../common/InputField";
import { ActionButton } from "../common/ActionButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faEnvelope,
  faLock,
} from "@fortawesome/free-solid-svg-icons";

// Valid SEATRC flags/countries from SectionHero
const flags = [
  { code: "bn", name: "Brunei" },
  { code: "kh", name: "Cambodia" },
  { code: "id", name: "Indonesia" },
  { code: "my", name: "Malaysia" },
  { code: "ph", name: "Philippines" },
  { code: "th", name: "Thailand" },
  { code: "vn", name: "Vietnam" },
  { code: "mm", name: "Myanmar" },
];

const loginSchema = z.object({
  email: z.string().email("Invalid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const resetPasswordSchema = z.object({
  email: z.string().email("Invalid email"),
});

const registerSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export const Login = () => {
  const navigate = useNavigate();
  const { user, logout: contextLogout } = useAuth();
  const [formMode, setFormMode] = useState("login");
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showInputs, setShowInputs] = useState(false);

  const {
    register: registerLogin,
    handleSubmit: handleSubmitLogin,
    setValue: setValueLogin,
    formState: { errors: errorsLogin, isSubmitting: isSubmittingLogin },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: errorsReset, isSubmitting: isSubmittingReset },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
  });

  const {
    register: registerReg,
    handleSubmit: handleSubmitReg,
    formState: { errors: errorsReg, isSubmitting: isSubmittingReg },
  } = useForm({
    resolver: zodResolver(registerSchema),
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (formMode === "login") {
      const verifiedEmail = localStorage.getItem("verifiedEmail");
      if (verifiedEmail) {
        setValueLogin("email", verifiedEmail);
        localStorage.removeItem("verifiedEmail");
      }
    }
  }, [formMode, setValueLogin]);

  useEffect(() => {
    if (user) {
      navigate("/profile");
    }
  }, [user, navigate]);

  useEffect(() => {
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [formMode]);

  const handleModeChange = (mode) => {
    setFormMode(mode);
    setErrorMessage(null);
    setSuccessMessage(null);
    if (mode === "login") {
      setShowInputs(false);
    }
  };

  const handleLogin = async (data) => {
    try {
      setErrorMessage(null);
      await contextLogout();
      const user = await login(data.email, data.password);

      if (!user.emailVerified) {
        setErrorMessage("Please verify your email before logging in.");
        await contextLogout();
        return;
      }

      navigate("/profile");
    } catch (error) {
      const errorCode =
        error?.code ||
        error?.message?.match(/\(auth\/(.*?)\)/)?.[1] ||
        "unknown_error";

      switch (errorCode) {
        case "user-not-found":
          setErrorMessage("Email not registered. Please register first.");
          break;
        case "wrong-password":
          setErrorMessage("Incorrect password. Please try again.");
          break;
        case "invalid-email":
          setErrorMessage("Invalid email format.");
          break;
        case "too-many-requests":
          setErrorMessage("Too many login attempts. Try again later.");
          break;
        case "invalid-credential":
          setErrorMessage("Incorrect email or password.");
          break;
        default:
          setErrorMessage("Email not verified. Please check your email.");
      }
    }
  };

  const handleGoogleLogin = async () => {
    setLoadingGoogle(true);
    setErrorMessage(null);
    try {
      await loginWithGoogle();
      navigate("/profile");
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleResetPassword = async (data) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await resetPassword(data.email);
      setSuccessMessage("Password reset link has been sent to your email.");
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const handleRegister = async (data) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const { message } = await registerAndSendVerification(
        data.email,
        data.name,
        data.password
      );
      setSuccessMessage(message);

      setTimeout(async () => {
        await checkVerificationAndDelete();
      }, 120000);

      setTimeout(() => {
        setFormMode("login");
        setShowInputs(false);
      }, 5000);
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  return (
    <div
      className="h-screen overflow-hidden flex items-center justify-center px-4 py-8 relative bg-fixed bg-cover bg-center"
      style={{
        backgroundImage: `url(${bgHero})`,
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-red-800 to-black/80 z-0"></div>

      <motion.div
        className="w-full max-w-5xl relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left Side - Brand & Features */}
          <motion.div
            className="hidden md:flex flex-col justify-center space-y-8"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div>
              {/* Using text or image for logo depending on available asset. Assuming we can reuse one of them */}
               <img src={"/logo/seatrc.svg"} alt="SEATRC Logo" className="h-32 object-contain mb-6" onError={(e) => {e.target.style.display='none'}} />
              
              <h1 className="text-5xl font-black text-white mb-3">SEATRC</h1>
              <p className="text-xl text-white font-light uppercase">
                Southeast Asian Trail Running Cup
              </p>
            </div>

            <div className="space-y-6">
          
              
               <motion.div
                  className="flex flex-wrap gap-3 pt-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {flags.map((flag) => (
                    <img
                      key={flag.code}
                      src={`https://flagcdn.com/w80/${flag.code}.png`}
                      srcSet={`https://flagcdn.com/w160/${flag.code}.png 2x`}
                      alt={flag.name}
                      title={flag.name}
                      className="w-10 rounded shadow-sm hover:scale-110 transition-transform duration-200"
                    />
                  ))}
                </motion.div>
            </div>

        
          </motion.div>

          {/* Right Side - Forms */}
          <motion.div
            className="bg-white rounded-2xl shadow-2xl p-6"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <AnimatePresence mode="wait">
              {formMode === "login" && (
                <motion.div
                  key="login"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div className="md:hidden mb-8">
                    <img src={JOTR} alt="Logo" className="w-16 h-auto mb-3" />
                    <h1 className="text-3xl font-black bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                      SEATRC
                    </h1>
                  </div>

                  <div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-2">
                      Welcome Back
                    </h2>
                    <p className="text-slate-500">
                      Sign in to your account to continue
                    </p>
                  </div>

                  {!showInputs && (
                    <motion.div
                      className="space-y-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      <ActionButton
                        type="button"
                        onClick={handleGoogleLogin}
                        variant="outline"
                        className="w-full disabled:opacity-50"
                        disabled={loadingGoogle}
                      >
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.51h5.84c-.25 1.37-1.02 2.53-2.16 3.31v2.75h3.49c2.04-1.88 3.23-4.65 3.23-7.77z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-1.01 7.28-2.73l-3.49-2.75c-1.01.68-2.3 1.08-3.79 1.08-2.91 0-5.37-1.97-6.25-4.61H2.36v2.9C4.17 20.99 7.83 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.75 14.24c-.22-.66-.34-1.37-.34-2.11s.12-1.45.34-2.11V7.12H2.36C1.49 8.71 1 10.34 1 12s.49 3.29 1.36 4.88l3.39-2.64z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.02c1.61 0 3.04.55 4.18 1.62l3.13-3.13C17.46 1.99 14.97 1 12 1 7.83 1 4.17 3.01 2.36 6.88l3.39 2.64c.88-2.64 3.34-4.5 6.25-4.5z"
                            fill="#EA4335"
                          />
                        </svg>
                        <span>
                          {loadingGoogle
                            ? "Processing..."
                            : "Continue with Google"}
                        </span>
                      </ActionButton>

                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-slate-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-slate-500">
                            Or
                          </span>
                        </div>
                      </div>

                      <ActionButton
                        type="button"
                        onClick={() => setShowInputs(true)}
                        className="w-full"
                      >
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="w-5 h-5"
                        />
                        <span>Sign in with Email</span>
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="w-4 h-4"
                        />
                      </ActionButton>
                    </motion.div>
                  )}

                  {showInputs && (
                    <motion.form
                      onSubmit={handleSubmitLogin(handleLogin)}
                      className="space-y-5"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: 0.3 }}
                    >
                      <div>
                        <label className="block text-sm font-semibold text-slate-900 mb-2">
                          Email Address
                        </label>
                        <div className="relative">
                          <FontAwesomeIcon
                            icon={faEnvelope}
                            className="absolute left-4 top-3.5 text-slate-400 w-4 h-4"
                          />
                          <input
                            type="email"
                            placeholder="you@example.com"
                            {...registerLogin("email")}
                            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-red-600 focus:ring-0 transition-colors"
                          />
                        </div>
                        {errorsLogin.email && (
                          <p className="text-red-600 text-sm mt-2">
                            {errorsLogin.email.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-900 mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <FontAwesomeIcon
                            icon={faLock}
                            className="absolute left-4 top-3.5 text-slate-400 w-4 h-4"
                          />
                          <input
                            type="password"
                            placeholder="••••••••"
                            {...registerLogin("password")}
                            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-red-600 focus:ring-0 transition-colors"
                          />
                        </div>
                        {errorsLogin.password && (
                          <p className="text-red-600 text-sm mt-2">
                            {errorsLogin.password.message}
                          </p>
                        )}
                      </div>

                      {errorMessage && (
                        <motion.div
                          className="bg-red-50 border-l-4 border-red-600 p-4 rounded"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                        >
                          <p className="text-sm text-red-600">{errorMessage}</p>
                        </motion.div>
                      )}

                      <button
                        type="submit"
                        className="w-full bg-red-600 text-white py-3 rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold disabled:opacity-50 flex items-center justify-center space-x-2"
                        disabled={isSubmittingLogin}
                      >
                        <span>
                          {isSubmittingLogin ? "Signing in..." : "Sign In"}
                        </span>
                        {!isSubmittingLogin && (
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className="w-4 h-4"
                          />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleModeChange("forgot-password")}
                        className="w-full text-red-600 hover:text-red-700 font-medium text-sm transition-colors"
                      >
                        Forgot password?
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowInputs(false)}
                        className="w-full text-slate-500 hover:text-slate-700 font-medium text-sm transition-colors"
                      >
                        ← Back
                      </button>
                    </motion.form>
                  )}

                  {!showInputs && (
                    <motion.div
                      className="pt-6 border-t border-slate-200 text-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <p className="text-slate-600 text-sm">
                        Don't have an account?{" "}
                        <button
                          type="button"
                          onClick={() => handleModeChange("register")}
                          className="text-red-600 hover:text-red-700 font-semibold transition-colors"
                        >
                          Create one now
                        </button>
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {formMode === "forgot-password" && (
                <motion.div
                  key="forgot-password"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-2">
                      Reset Password
                    </h2>
                    <p className="text-slate-500">
                      Enter your email to receive a reset link
                    </p>
                  </div>

                  <form
                    onSubmit={handleSubmitReset(handleResetPassword)}
                    className="space-y-5"
                  >
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="absolute left-4 top-3.5 text-slate-400 w-4 h-4"
                        />
                        <input
                          type="email"
                          placeholder="you@example.com"
                          {...registerReset("email")}
                          className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-red-600 focus:ring-0 transition-colors"
                        />
                      </div>
                      {errorsReset.email && (
                        <p className="text-red-600 text-sm mt-2">
                          {errorsReset.email.message}
                        </p>
                      )}
                    </div>

                    {errorMessage && (
                      <motion.div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
                        <p className="text-sm text-red-600">{errorMessage}</p>
                      </motion.div>
                    )}

                    {successMessage && (
                      <motion.div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                        <p className="text-sm text-green-600">
                          {successMessage}
                        </p>
                      </motion.div>
                    )}

                    <button
                      type="submit"
                      className="w-full bg-red-600 text-white py-3 rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold disabled:opacity-50"
                      disabled={isSubmittingReset}
                    >
                      {isSubmittingReset ? "Sending..." : "Send Reset Link"}
                    </button>
                  </form>

                  <button
                    type="button"
                    onClick={() => handleModeChange("login")}
                    className="w-full text-slate-600 hover:text-slate-900 font-medium text-sm transition-colors"
                  >
                    ← Back to Login
                  </button>
                </motion.div>
              )}

              {formMode === "register" && (
                <motion.div
                  key="register"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-2">
                      Create Account
                    </h2>
                    <p className="text-slate-500">
                      Join the SEATRC community today
                    </p>
                  </div>

                  <form
                    onSubmit={handleSubmitReg(handleRegister)}
                    className="space-y-5"
                  >
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        placeholder="John Doe"
                        {...registerReg("name")}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-red-600 focus:ring-0 transition-colors"
                      />
                      {errorsReg.name && (
                        <p className="text-red-600 text-sm mt-2">
                          {errorsReg.name.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="absolute left-4 top-3.5 text-slate-400 w-4 h-4"
                        />
                        <input
                          type="email"
                          placeholder="you@example.com"
                          {...registerReg("email")}
                          className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-red-600 focus:ring-0 transition-colors"
                        />
                      </div>
                      {errorsReg.email && (
                        <p className="text-red-600 text-sm mt-2">
                          {errorsReg.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faLock}
                          className="absolute left-4 top-3.5 text-slate-400 w-4 h-4"
                        />
                        <input
                          type="password"
                          placeholder="••••••••"
                          {...registerReg("password")}
                          className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-red-600 focus:ring-0 transition-colors"
                        />
                      </div>
                      {errorsReg.password && (
                        <p className="text-red-600 text-sm mt-2">
                          {errorsReg.password.message}
                        </p>
                      )}
                    </div>

                    {errorMessage && (
                      <motion.div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
                        <p className="text-sm text-red-600">{errorMessage}</p>
                      </motion.div>
                    )}

                    {successMessage && (
                      <motion.div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                        <p className="text-sm text-green-600">
                          {successMessage}
                        </p>
                      </motion.div>
                    )}

                    <button
                      type="submit"
                      className="w-full bg-red-600 text-white py-3 rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold disabled:opacity-50"
                      disabled={isSubmittingReg}
                    >
                      {isSubmittingReg ? "Creating..." : "Create Account"}
                    </button>
                  </form>

                  <button
                    type="button"
                    onClick={() => handleModeChange("login")}
                    className="w-full text-slate-600 hover:text-slate-900 font-medium text-sm transition-colors"
                  >
                    ← Back to Login
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};
